import { Capacitor } from '@capacitor/core';
import { useSyncExternalStore } from 'react';
import {
  initializePurchases,
  identifyUser,
  purchasePackage as nativePurchasePackage,
  restorePurchases as nativeRestorePurchases,
  PurchasePendingError,
} from '@tupai/billing';
import { getPendingPurchase } from './pending-purchase-store';
import { useAuthStore } from '@/store/auth-store';

let initialized = false;
let initialization: Promise<void> | undefined;
let operations: Promise<unknown> = Promise.resolve();
const listeners = new Set<() => void>();

export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_PARENT_STRIPE_PUBLISHABLE_KEY as
  string | undefined;

export function billingAvailable(): boolean {
  return initialized;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBillingAvailable(): boolean {
  return useSyncExternalStore(subscribe, billingAvailable);
}

export function initBilling(): Promise<void> {
  if (initialization) return initialization;
  initialization = configureBilling().catch((error) => {
    initialization = undefined;
    throw error;
  });
  return initialization;
}

async function configureBilling(): Promise<void> {
  const platform = Capacitor.getPlatform();

  const keyName = {
    ios: 'VITE_PARENT_REVENUECAT_APPLE_API_KEY',
    android: 'VITE_PARENT_REVENUECAT_PLAY_API_KEY',
    web: 'VITE_PARENT_STRIPE_PUBLISHABLE_KEY',
  }[platform];

  const apiKey = {
    ios: import.meta.env.VITE_PARENT_REVENUECAT_APPLE_API_KEY,
    android: import.meta.env.VITE_PARENT_REVENUECAT_PLAY_API_KEY,
    web: STRIPE_PUBLISHABLE_KEY,
  }[platform];

  if (!apiKey) {
    console.warn(`[billing] ${keyName} not set — purchases disabled`);
    return;
  }

  if (platform === 'ios' || platform === 'android') {
    await initializePurchases(apiKey);
  }

  initialized = true;
  listeners.forEach((listener) => listener());
}

// Serialize identity changes with store operations so a later login cannot retarget an open purchase.
function enqueue<T>(operation: () => Promise<T>): Promise<T> {
  const result = operations.then(operation);
  operations = result.catch(() => {});
  return result;
}

export class BillingIdentityError extends Error {
  constructor() {
    super('Billing identity could not be verified');
    this.name = 'BillingIdentityError';
  }
}

async function identifyCurrentParent(parentId: string): Promise<void> {
  const isCurrent = () => {
    const session = useAuthStore.getState();
    return Boolean(session.token) && session.parentId === parentId;
  };
  try {
    await initBilling();
    if (!initialized || !isCurrent()) throw new BillingIdentityError();
    await identifyUser(parentId);
  } catch {
    throw new BillingIdentityError();
  }
  if (!isCurrent()) throw new BillingIdentityError();
}

export async function identifyBillingUser(parentId: string): Promise<void> {
  if (Capacitor.getPlatform() === 'web') return;
  try {
    await enqueue(() => identifyCurrentParent(parentId));
  } catch {
    console.warn('[billing] parent identity unavailable — purchase and restore require a retry');
  }
}

async function withBillingIdentity<T>(operation: () => Promise<T>): Promise<T> {
  const { parentId, token } = useAuthStore.getState();
  return enqueue(async () => {
    if (!parentId || !token) {
      throw new BillingIdentityError();
    }

    await identifyCurrentParent(parentId);
    const current = useAuthStore.getState();
    if (current.parentId !== parentId || current.token !== token) {
      throw new BillingIdentityError();
    }
    return operation();
  });
}

export function purchasePackage(...args: Parameters<typeof nativePurchasePackage>) {
  return withBillingIdentity(() => {
    const { parentId } = useAuthStore.getState();
    if (parentId && getPendingPurchase(parentId)) throw new PurchasePendingError();
    return nativePurchasePackage(...args);
  });
}

export function restorePurchases() {
  return withBillingIdentity(() => nativeRestorePurchases());
}
